import { AssistantChat } from "@/components/assistant/AssistantChat";
import { NavIconBadge } from "@/components/ui/NavIconBadge";
import { ALL_SECTIONS, PLATFORM_NAME } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { MessageCircle, Search, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

type Mode = "ask" | "go";

/** Session-only drag position (clears on new tab / browser restart — simple). */
const FAB_POS_KEY = "motoko.askAi.fabPos.session";
const FAB_SIZE = 52;
/** Desktop: agent band + play/local sandbox strip under it. */
const AGENT_BAND_PX = 56;
const PLAY_BANNER_PX = 40;
const TAB_BAR_PX = 72;

type FabPos = { x: number; y: number };

function loadFabPos(): FabPos | null {
	try {
		const raw = sessionStorage.getItem(FAB_POS_KEY);
		if (!raw) return null;
		const p = JSON.parse(raw) as FabPos;
		if (typeof p.x === "number" && typeof p.y === "number") return p;
	} catch {
		/* ignore */
	}
	return null;
}

function saveFabPos(p: FabPos) {
	try {
		sessionStorage.setItem(FAB_POS_KEY, JSON.stringify(p));
	} catch {
		/* ignore */
	}
}

function clearFabPos() {
	try {
		sessionStorage.removeItem(FAB_POS_KEY);
	} catch {
		/* ignore */
	}
}

function bottomReservePx(w: number): number {
	const md = w >= 768;
	// Mobile: tabs + soft env strip (~1.25rem); desktop: agent + full play banner
	const mobileSoft = 22;
	return md
		? AGENT_BAND_PX + PLAY_BANNER_PX + 16
		: TAB_BAR_PX + mobileSoft + 12;
}

function defaultFabPos(): FabPos {
	const w = typeof window !== "undefined" ? window.innerWidth : 400;
	const h = typeof window !== "undefined" ? window.innerHeight : 800;
	const bottomReserve = bottomReservePx(w);
	return {
		x: Math.max(16, w - 120),
		y: Math.max(16, h - bottomReserve - FAB_SIZE),
	};
}

function clampPos(p: FabPos): FabPos {
	const w = window.innerWidth;
	const h = window.innerHeight;
	const bottomReserve = bottomReservePx(w);
	const maxX = Math.max(8, w - 130);
	const maxY = Math.max(8, h - bottomReserve - FAB_SIZE);
	return {
		x: Math.min(maxX, Math.max(8, p.x)),
		y: Math.min(maxY, Math.max(8, p.y)),
	};
}

/**
 * Protocol assistant FAB — Ask (chat) + Go (navigate).
 * Portaled to body; draggable; stays above agent band / mobile tabs.
 */
export function AiWidget() {
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<Mode>("ask");
	const [q, setQ] = useState("");
	const navigate = useNavigate();
	const [mounted, setMounted] = useState(false);
	const [pos, setPos] = useState<FabPos>(() => loadFabPos() ?? defaultFabPos());
	const dragRef = useRef<{
		active: boolean;
		moved: boolean;
		ox: number;
		oy: number;
		sx: number;
		sy: number;
	} | null>(null);

	useEffect(() => {
		setMounted(true);
		setPos((p) => clampPos(p));
	}, []);

	useEffect(() => {
		const onResize = () => setPos((p) => clampPos(p));
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		if (!open) {
			setQ("");
			setMode("ask");
			return;
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("keydown", onKey);
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = prev;
		};
	}, [open]);

	const goHits = useMemo(() => {
		const needle = q.trim().toLowerCase();
		if (!needle) return ALL_SECTIONS;
		return ALL_SECTIONS.filter(
			(s) =>
				s.label.toLowerCase().includes(needle) ||
				s.desc.toLowerCase().includes(needle) ||
				s.to.toLowerCase().includes(needle),
		);
	}, [q]);

	const onPointerDown = useCallback(
		(e: React.PointerEvent) => {
			if (e.button !== 0) return;
			const el = e.currentTarget as HTMLElement;
			el.setPointerCapture(e.pointerId);
			dragRef.current = {
				active: true,
				moved: false,
				ox: e.clientX,
				oy: e.clientY,
				sx: pos.x,
				sy: pos.y,
			};
		},
		[pos.x, pos.y],
	);

	const onPointerMove = useCallback((e: React.PointerEvent) => {
		const d = dragRef.current;
		if (!d?.active) return;
		const dx = e.clientX - d.ox;
		const dy = e.clientY - d.oy;
		if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
		if (!d.moved) return;
		e.preventDefault();
		setPos(clampPos({ x: d.sx + dx, y: d.sy + dy }));
	}, []);

	const onPointerUp = useCallback(
		(e: React.PointerEvent) => {
			const d = dragRef.current;
			dragRef.current = null;
			try {
				(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
			} catch {
				/* ignore */
			}
			if (!d) return;
			if (d.moved) {
				setPos((p) => {
					const c = clampPos(p);
					saveFabPos(c);
					return c;
				});
				return;
			}
			setOpen(true);
		},
		[],
	);

	const resetPos = useCallback(() => {
		clearFabPos();
		const p = clampPos(defaultFabPos());
		setPos(p);
	}, []);

	const fab = (
		<button
			type="button"
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			className={cn(
				"fixed z-[60] inline-flex touch-none items-center gap-2 rounded-full bg-primary px-4 py-3",
				"cursor-grab text-sm font-semibold text-primary-foreground shadow-neon active:cursor-grabbing",
				"hover:bg-primary-hover active:scale-[0.98]",
			)}
			style={{ left: pos.x, top: pos.y, right: "auto", bottom: "auto" }}
			aria-label="Open Protocol Assistant (drag to move)"
			title="Drag to move · click to open"
		>
			<Sparkles className="size-4" aria-hidden />
			Ask AI
		</button>
	);

	const panel = open ? (
		<div
			className="fixed inset-0 z-[70] flex items-end justify-end bg-black/45 backdrop-blur-sm sm:items-stretch"
			role="dialog"
			aria-modal="true"
			aria-label="Protocol Assistant"
			onClick={() => setOpen(false)}
		>
			<div
				className={cn(
					"flex w-full flex-col border-border bg-card shadow-lift",
					"h-[min(92dvh,100%)] max-h-[100dvh] rounded-t-2xl border-t",
					"sm:ml-auto sm:h-full sm:max-h-none sm:w-full sm:max-w-md sm:rounded-none sm:border-l sm:border-t-0",
				)}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
					<div>
						<p className="text-sm font-bold">Ask AI</p>
						<p className="text-[10px] text-muted-foreground">
							{PLATFORM_NAME} · chat or go to a section
						</p>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="rounded-md border border-border px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-muted"
							onClick={resetPos}
							title="Return Ask AI to the default corner (drag to move anytime)"
						>
							Reset position
						</button>
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
							aria-label="Close"
						>
							<X className="size-4" />
						</button>
					</div>
				</div>
				<div className="flex shrink-0 gap-1 border-b border-border p-2">
					{(
						[
							["ask", "Ask", MessageCircle],
							["go", "Go", Search],
						] as const
					).map(([id, label, Icon]) => (
						<button
							key={id}
							type="button"
							onClick={() => setMode(id)}
							className={cn(
								"inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold",
								mode === id
									? "bg-primary/15 text-primary"
									: "text-muted-foreground hover:bg-muted",
							)}
						>
							<Icon className="size-3.5" aria-hidden />
							{label}
						</button>
					))}
				</div>
				{mode === "ask" ? (
					<div className="flex min-h-0 flex-1 flex-col">
						<AssistantChat />
					</div>
				) : (
					<div className="flex min-h-0 flex-1 flex-col p-3">
						<input
							value={q}
							onChange={(e) => setQ(e.target.value)}
							placeholder="Go to section…"
							className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
							// biome-ignore lint/a11y/noAutofocus: panel open intent
							autoFocus
						/>
						<ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1 pb-[max(1rem,env(safe-area-inset-bottom))]">
							{goHits.map((s) => (
								<li key={s.to}>
									<button
										type="button"
										className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted"
										onClick={() => {
											navigate(s.to);
											setOpen(false);
										}}
									>
										<NavIconBadge
											className="mt-0.5"
											icon={s.icon}
											size="sm"
										/>
										<span className="min-w-0">
											<span className="block text-sm font-semibold">
												{s.label}
											</span>
											<span className="block text-[11px] text-muted-foreground">
												{s.desc}
											</span>
										</span>
									</button>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</div>
	) : null;

	if (!mounted) return null;
	return createPortal(
		<>
			{!open ? fab : null}
			{panel}
		</>,
		document.body,
	);
}
